import React, { useState, useEffect, useCallback } from "react";
import CloseIcon from "@mui/icons-material/Close";
import AssessmentIcon from '@mui/icons-material/Assessment';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import InsightsIcon from '@mui/icons-material/Insights';
import { AppraisalTab } from "./tabs/AppraisalTab";
import { ResultsTab } from "./tabs/ResultsTab";
import axios from '../../../../api/apiClient';
import { token, domain } from '../../../Security';

export function PerformanceAppraisalForm({ isOpen, onClose, selectedData, showGeneralMessage, user, fetchMainData }) {
  // 1. Initial States
  const [activeTab, setActiveTab] = useState("appraisal");
  const [evaluationData, setEvaluationData] = useState({}); // Stores { [objId]: { score: 0, comment: "" } }
  const [legendDefinitions, setLegendDefinitions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 2. Fetch Legend Logic (Defined as useCallback to prevent hoisting errors)
  const fetchLegendDetails = useCallback(async () => {
    try {
      const response = await axios.get(`${domain}/api/PerformanceRatingLegendGroup/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const currentLegend = response.data.find(l => l.id === selectedData.performanceRatingsLegendId);
      setLegendDefinitions(currentLegend?.performanceRatingDefinitions || []);
    } catch (error) {
      console.error("Error fetching legends", error);
      showGeneralMessage("Failed to load rating legend definitions.", false);
    }
  }, [selectedData?.performanceRatingsLegendId, showGeneralMessage]);

  // 3. Effect for Reset and Initialization
  useEffect(() => {
    if (!isOpen) {
      setEvaluationData({});
      setActiveTab("appraisal");
      setLegendDefinitions([]);
      setIsSubmitting(false);
    } else if (selectedData) {
      // A. Fetch Legend Definitions
      if (selectedData.performanceRatingsLegendId) {
        fetchLegendDetails();
      }

      // B. LOAD EXISTING SCORES INTO STATE
      if (selectedData.scores && selectedData.scores.length > 0) {
        const existingScoresMap = {};

        selectedData.scores.forEach(item => {
          existingScoresMap[item.performanceObjectiveId] = {
            score: item.score,
            comment: item.comment // CHANGED from item.comments to item.comment
          };
        });

        setEvaluationData(existingScoresMap);
      } else {
        setEvaluationData({});
      }
    }
  }, [isOpen, selectedData, fetchLegendDetails]);

  // 4. Handlers
  const handleEvaluationChange = (objectiveId, field, value) => {
    setEvaluationData(prev => ({
      ...prev,
      [objectiveId]: {
        ...prev[objectiveId],
        [field]: field === 'score' ? parseFloat(value) : value
      }
    }));
  };

  const handleInternalClose = () => {
    setEvaluationData({});
    onClose();
  };

  const handleSubmitEvaluation = async () => {
    // 1. Validation: Ensure all objectives in the categories are rated
    const totalObjectives = selectedData.performanceCategories.reduce(
      (sum, cat) => sum + (cat.objectives?.length || 0), 0
    );

    const ratedCount = Object.values(evaluationData).filter(v => v.score > 0).length;

    if (ratedCount < totalObjectives) {
      showGeneralMessage(`Incomplete: Please provide a rating for all objectives.`, false);
      return;
    }

    try {
      setIsSubmitting(true);

      // 2. Prepare Payload
      const payload = {
        kpiPerformanceAppraisalId: selectedData.id,
        evaluatedById: user.id,
        status: "Processing", // Or keep it as selectedData.status if you don't want to change it
        scores: Object.keys(evaluationData).map(objId => {

          // Find the specific objective to get its weight for the computation
          let objectiveWeight = 0;
          selectedData.performanceCategories.forEach(cat => {
            const found = cat.objectives.find(o => o.id === parseInt(objId));
            if (found) objectiveWeight = found.percentage;
          });

          const rawScore = parseFloat(evaluationData[objId].score);

          return {
            performanceObjectiveId: parseInt(objId),
            score: rawScore,
            // Calculate the weighted score: (Rating * (Weight/100))
            computedScore: parseFloat((rawScore * (objectiveWeight / 100)).toFixed(2)),
            comment: evaluationData[objId].comment || ""
          };
        })
      };

      // 3. POST to the API
      // This will trigger the RemoveRange -> Add logic in your Controller
      await axios.post(`${domain}/api/KpiPerformanceAppraisal/submit-evaluation`, payload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (fetchMainData) {
        await fetchMainData(); 
      }

      // 4. Refresh the table in the main list
      // (Assuming you passed the fetch function or use a window reload)
      onClose();
      showGeneralMessage("Evaluation updated successfully!", true);

    } catch (error) {
      console.error("Update Error:", error);
      showGeneralMessage(error.response?.data?.message || "Failed to update evaluation.", false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-3">
      <div className="relative flex h-auto max-h-[95vh] w-full max-w-6xl flex-col rounded-lg bg-gray-50 shadow-xl">

        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-2 shadow-sm">
          <h2 className="flex items-center space-x-2 text-xl font-semibold text-gray-800">
            <AssessmentIcon className="text-orange-500" />
            <span className="text-xl text-gray-600 tracking-tight">
              Evaluation: {selectedData?.employeeName}
            </span>
          </h2>
          <button
            onClick={handleInternalClose}
            className="border-none bg-transparent p-1 text-gray-400 transition-colors hover:text-gray-700 cursor-pointer"
          >
            <CloseIcon fontSize="medium" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 bg-white pt-3">
          <div className="flex space-x-1 px-6">
            <button
              onClick={() => setActiveTab("appraisal")}
              className={`px-4 py-3 text-sm font-medium rounded-t-lg transition-colors border-b-2 ${activeTab === "appraisal"
                ? "border-blue-500 text-blue-600 bg-blue-50"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
            >
              <FactCheckIcon fontSize="small" className="mr-2" />
              Appraisal Form
            </button>
            <button
              onClick={() => setActiveTab("results")}
              className={`px-4 py-3 text-sm font-medium rounded-t-lg transition-colors border-b-2 ${activeTab === "results"
                ? "border-blue-500 text-blue-600 bg-blue-50"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
            >
              <InsightsIcon fontSize="small" className="mr-2" />
              Summary & Results
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-white/50">
          {activeTab === "appraisal" ? (
            <AppraisalTab
              data={selectedData}
              evaluationData={evaluationData}
              onEvaluationChange={handleEvaluationChange}
              legendDefinitions={legendDefinitions}
            />
          ) : (
            <ResultsTab
              data={selectedData}
              evaluationData={evaluationData}
              legendDefinitions={legendDefinitions}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-2 rounded-b-lg border-t border-gray-200 bg-white p-5 shadow-sm">
          <button
            onClick={handleInternalClose}
            disabled={isSubmitting}
            className="cursor-pointer rounded border-none bg-gray-400 px-4 py-2 text-white transition-colors hover:bg-gray-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmitEvaluation}
            disabled={isSubmitting}
            className="cursor-pointer rounded border-none bg-blue-500 px-6 py-2 text-white font-bold transition-colors hover:bg-blue-600 shadow-md disabled:opacity-50"
          >
            {isSubmitting ? "Processing..." :
              (selectedData.scores && selectedData.scores.length > 0 ? "Update Evaluation" : "Submit Final Evaluation")}
          </button>
        </div>
      </div>
    </div>
  );
}